/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {ReportPageTitle} from './PageTitle/ReportPageTitle'
import {useAuth} from '../../../modules/auth'
import {collection, doc, onSnapshot} from 'firebase/firestore'
import db from '../../../../services/firebase'
import {useParams} from 'react-router-dom'
import {ViewControl} from '../../controls/components/ViewControl'
import {Helmet} from 'react-helmet'
import {TestListLoading} from '../../tests/components/loading/TestListLoading'
import {saveAs} from 'file-saver'
import ExcelJS from 'exceljs'
import {ToastContainer} from 'react-toastify'
import useCrispChat from 'app/Hooks/useCrispChat'
import useAuditContext from 'app/Hooks/useAuditContext'
import UpgradePlan from 'app/components/UpgradePlan'
import {showSuccessToast} from 'app/components/ShowToast'
import {processControlValues} from 'app/pages/controls/utils/controlUtils'
import Searchbar from 'app/components/Searchbar'
import Button from 'app/components/Button'

interface Control {
  id: string
  control_category: string
  control_description: string
  control_document: string
  control_evidence_status: string
  control_framework_mappings: string
  control_id: string
  control_owner: string
  control_test: string
  control_title: string
  control_uid: string
  document_id: string[]
  test_id: string[]
  deactivation_control: string
}

const getCodeFromFrameworkMapping = (mapping: string) => {
  const parts = mapping.split('‧')
  return parts.length > 1 ? parts[1].trim() : ''
}

const getMainCode = (mapping: string) => {
  const parts = mapping.split('‧')
  if (parts.length > 1) {
    return parts[1].trim().split('.').slice(0, 2).join('.')
  }
  return ''
}

const getLetterPrefix = (code: string) => {
  return code.match(/[A-Z]/)?.[0] || ''
}

const ViewReports = () => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const {currentUser} = useAuth()
  const [controls, setControls] = useState<Record<string, Control[]>>({})
  const [filteredControls, setFilteredControls] = useState<Record<string, Control[]>>({})
  const {name} = useParams()
  const [open, setOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [controlID, setControlID] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const {toggleCrispChat} = useCrispChat()
  const [showModal, setShowModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [tests, setTests] = useState([] as any[])
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    getControl()
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 8000)
    return () => clearTimeout(timeoutId)
  }, [])

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id)
    setOpen(!open)
  }

  const handleOpenModal = (id) => {
    setIsModalOpen(true)
    if (!id) {
      return
    }
    setControlID(id)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  useEffect(() => {
    const filterControls = () => {
      let filtered = {...controls}

      if (searchTerm.trim() !== '') {
        filtered = Object.entries(controls).reduce((acc, [mainCode, groupedControls]) => {
          const filtered = groupedControls.filter((control) =>
            control.control_title.toLowerCase().includes(searchTerm.toLowerCase())
          )
          if (filtered.length > 0) {
            acc[mainCode] = filtered
          }
          return acc
        }, {} as Record<string, Control[]>)
      }

      if (selectedCategory !== 'all') {
        filtered = Object.entries(filtered).reduce((acc, [mainCode, groupedControls]) => {
          const currentCategory = `${mainCode.split(',')[0].trim()} ${
            controls[mainCode][0].control_category
          }`
          if (currentCategory === selectedCategory) {
            acc[mainCode] = groupedControls
          }
          return acc
        }, {} as Record<string, Control[]>)
      }

      setSearchLength(Object.keys(filtered).length === 0)
      setFilteredControls(filtered)
    }

    filterControls()
  }, [searchTerm, controls, selectedCategory])

  const getControl = async () => {
    let unsubscribeTests: (() => void) | undefined
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const controlsCollectionRef = collection(tenantRef, 'controls')
      const testsRef = collection(tenantRef, 'tests')

      unsubscribeTests = onSnapshot(testsRef, (snapshot) => {
        const testsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data().status,
        }))
        setTests(testsData)
      })

      const unsubscribe = onSnapshot(controlsCollectionRef, (snapshot) => {
        let controlsData: Control[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          control_category: doc.data().control_category || '',
          control_description: doc.data().control_description || '',
          control_document: doc.data().control_document || '',
          control_evidence_status: doc.data().control_evidence_status || '',
          control_framework_mappings: doc.data().control_framework_mappings || '',
          control_id: doc.data().control_id || '',
          control_owner: doc.data().control_owner || '',
          control_test: doc.data().control_test || '',
          control_title: doc.data().control_title || '',
          control_uid: doc.data().control_uid || '',
          document_id: doc.data().document_id || [],
          test_id: doc.data().test_id || [],
          deactivation_control: doc.data().deactivation_control || '',
        }))

        const filteredControls = controlsData.filter(
          (control) =>
            control.control_framework_mappings.startsWith(`${name}`) &&
            !control.control_framework_mappings.startsWith('ISO 27001:2013')
        )

        const groupedControls = filteredControls.reduce((acc, control) => {
          const mainCode = getMainCode(control.control_framework_mappings)
          if (!acc[mainCode]) {
            acc[mainCode] = []
          }
          acc[mainCode].push(control)
          return acc
        }, {} as Record<string, Control[]>)

        Object.keys(groupedControls).forEach((mainCode) => {
          groupedControls[mainCode].sort((a, b) => {
            const codeA = getCodeFromFrameworkMapping(a.control_framework_mappings)
            const codeB = getCodeFromFrameworkMapping(b.control_framework_mappings)
            return codeA.localeCompare(codeB)
          })
        })

        const getNumericParts = (code: string) => {
          return code.match(/\d+/g)?.map(Number) || []
        }

        const sortedGroupedControls = Object.entries(groupedControls)
          .sort(([keyA], [keyB]) => {
            const prefixA = getLetterPrefix(keyA)
            const prefixB = getLetterPrefix(keyB)
            if (prefixA === 'C' && prefixB !== 'C') {
              return -1
            } else if (prefixB === 'C' && prefixA !== 'C') {
              return 1
            }
            if (prefixA !== prefixB) {
              return prefixA.localeCompare(prefixB)
            }
            const numericPartsA = getNumericParts(keyA)
            const numericPartsB = getNumericParts(keyB)
            for (let i = 0; i < Math.min(numericPartsA.length, numericPartsB.length); i++) {
              if (numericPartsA[i] !== numericPartsB[i]) {
                return numericPartsA[i] - numericPartsB[i]
              }
            }
            return numericPartsA.length - numericPartsB.length
          })
          .reduce((acc, [key, value]) => {
            acc[key] = value.sort((a, b) => {
              const codeA = getCodeFromFrameworkMapping(a.control_framework_mappings)
              const codeB = getCodeFromFrameworkMapping(b.control_framework_mappings)
              const numericPartsA = getNumericParts(codeA)
              const numericPartsB = getNumericParts(codeB)
              for (let i = 0; i < Math.min(numericPartsA.length, numericPartsB.length); i++) {
                if (numericPartsA[i] !== numericPartsB[i]) {
                  return numericPartsA[i] - numericPartsB[i]
                }
              }
              return numericPartsA.length - numericPartsB.length
            })
            return acc
          }, {} as Record<string, Control[]>)

        if (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') {
          const allControls = Object.values(sortedGroupedControls).flat()

          allControls.forEach((control, index) => {
            if (index >= 4) {
              control.control_title = maskStringPreservingSpaces(control.control_title)
            }
          })
        }

        setControls(sortedGroupedControls)
        setLoading(false)
      })

      return () => {
        if (unsubscribe) {
          unsubscribe()
        }
        if (unsubscribeTests) {
          unsubscribeTests()
        }
      }
    } catch (error) {
      console.error('Error getting controls:', error)
    }
  }

  const maskStringPreservingSpaces = (str, visibleStartLength = 4, visibleEndLength = 3) => {
    if (!str || str.length <= visibleStartLength + visibleEndLength) return '****'

    const start = str.slice(0, visibleStartLength)
    const end = str.slice(-visibleEndLength)
    const middle = str.slice(visibleStartLength, -visibleEndLength)

    const maskedMiddle = [...middle].map((char) => (char === ' ' ? ' ' : '*')).join('')

    return `${start}${maskedMiddle}${end}`
  }

  const stripHtmlTags = (html) => {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const getUniqueCategories = () => {
    const categories = new Set<string>()
    Object.keys(controls).forEach((mainCode) => {
      const category = `${mainCode.split(',')[0].trim()} ${controls[mainCode][0].control_category}`
      categories.add(category)
    })
    return ['all', ...Array.from(categories)]
  }

  const exportData = async () => {
    if (Object.keys(filteredControls).length > 0) {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Controls')

      // Define header row
      worksheet.columns = [
        {header: 'Control', key: 'controlObjective', width: 50},
        {header: 'Control Description', key: 'controlDescription', width: 60},
        {header: 'Framework', key: 'sec', width: 30},
        {header: 'Category', key: 'clause', width: 30},
        {header: 'Platform', key: 'platform', width: 15},
        {header: 'Document', key: 'document', width: 15},
        {header: 'Status', key: 'status', width: 15},
        {header: 'Justification for exclusion', key: 'justification', width: 50},
      ]

      const headerRow = worksheet.getRow(1)
      headerRow.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 10}
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {argb: 'FF000000'},
      }
      headerRow.height = 30
      headerRow.alignment = {horizontal: 'center', vertical: 'middle'}
      worksheet.views = [{state: 'frozen', ySplit: 1}]

      const exportData = Object.values(filteredControls)
        .flatMap((group) => group)
        .sort((a, b) => (a.control_title || '').localeCompare(b.control_title || ''))
        .map((row) => ({
          controlObjective: row.control_title || '',
          controlDescription: stripHtmlTags(row.control_description) || '',
          sec: row.control_framework_mappings || '',
          clause: row.control_category,
          platform: row.control_test || '',
          document: row.control_document || '',
          status: row.control_evidence_status || '',
          justification: row.deactivation_control || '',
        }))

      exportData.forEach((item) => {
        const row = worksheet.addRow(item)
        row.height = 20
        row.font = {size: 10}

        if (item.status === 'Deactivated') {
          row.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'DDEEFF'},
            }
          })
        }

        if (item.status === 'OK') {
          row.getCell('G').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'D9EAD3'},
          }
        } else if (item.status === 'Unhealthy') {
          row.getCell('G').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'F4CCCC'},
          }
        }
      })

      const buffer = await workbook.xlsx.writeBuffer()
      const excelBlob = new Blob([buffer], {type: 'application/octet-stream'})
      saveAs(excelBlob, `controls_${name}.xlsx`)
      showSuccessToast({message: 'Controls data exported successfully'})
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Framework</title>
      </Helmet>
      {name === 'SOC 2' ? (
        <ReportPageTitle
          icon='SOC.svg'
          title='SOC 2'
          desc='Your progress towards SOC 2 compliance based on your controls and tests.'
        ></ReportPageTitle>
      ) : (
        <ReportPageTitle
          icon='ISO.svg'
          title='ISO 27001: 2022'
          desc='Your progress towards ISO 27001:2022 compliance based on your controls and tests.'
        ></ReportPageTitle>
      )}
      {(currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') && (
        <div className='card' style={{backgroundColor: '#F3FAF7', border: '1.5px solid #0E9F6E'}}>
          <div className='p-6 fs-6'>
            <div className='ms-4 fw-semibold d-flex align-items-center justify-content-between'>
              <div>
                <h5 className='fw-bold text-dark mb-2'>Advance Your Security Program</h5>
                <p className='mb-0 text-gray-700 fs-7'>
                  Available on any paid plan, this section outlines essential clauses and controls
                  for building an effective ISMS.
                </p>
              </div>
              <Button btnType='primary' onClick={toggleCrispChat}>
                Contact sales
              </Button>
            </div>
          </div>
        </div>
      )}
      <div>
        <div className='card-header border-0 pt-6 h-100 mb-8'>
          {currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus' && (
            <div className='d-flex justify-content-between align-items-center w-100'>
              <div className='flex-lg-fill'>
                <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
              </div>
              <div className='d-flex'>
                {currentUser?.account_type !== 'Trial' && currentUser?.account_type !== 'Trial-plus' && (
                  <div className='d-flex align-items-center mx-4'>
                    <select
                      className='form-select form-select-sm'
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      style={{
                        minWidth: '200px',
                        height: '40px',
                        borderRadius: '6px',
                      }}
                    >
                      {getUniqueCategories().map((category) => (
                        <option key={category} value={category}>
                          {category === 'all' ? 'All Categories' : category}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {currentUser?.role !== 'Auditor' && (
                  <button
                    type='button'
                    onClick={exportData}
                    className='btn btn-sm fs-6'
                    style={{height: '40px', background: '#0E9F6E', color: '#fff'}}
                    onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#148363')
                    }
                    onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                      (e.currentTarget.style.backgroundColor = '#0E9F6E')
                    }
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      className='icon icon-tabler icons-tabler-outline icon-tabler-download mb-1'
                      style={{marginRight: '4px'}}
                    >
                      <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                      <path d='M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2' />
                      <path d='M7 11l5 5l5 -5' />
                      <path d='M12 4l0 12' />
                    </svg>
                    Export
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <ViewControl isOpen={isModalOpen} onClose={handleCloseModal} id={controlID} />
        {loading ? (
          <div
            className='d-flex justify-content-center align-items-center text-center'
            style={{
              height: '100vh',
              width: '100vw',
              position: 'absolute',
              top: '20vh',
              left: '20vh',
            }}
          >
            <TestListLoading />
          </div>
        ) : Object.keys(filteredControls).length > 0 ? (
          Object.keys(filteredControls).map((mainCode, index) => {
            const categoryCount = Object.keys(filteredControls)
              .slice(0, index + 1)
              .filter((code, i, array) => {
                return i === 0 || code !== array[i - 1]
              }).length

            return (
              <div key={mainCode} className='mt-4'>
                <span className='fs-5 fw-bold '>
                  {mainCode.split(',')[0].trim()} {controls[mainCode][0].control_category}
                </span>
                {filteredControls[mainCode]
                  .sort((a, b) => (a.control_title || '').localeCompare(b.control_title || ''))
                  .map((control) => (
                  <>
                    <div
                      key={control.id}
                      className={`card p-6 mt-4 mb-4`}
                      style={
                        (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus') && categoryCount > 1
                          ? {filter: 'blur(4px)', pointerEvents: 'none'}
                          : {}
                      }
                    >
                      <div
                        className='d-flex'
                        onClick={() => toggleAccordion(control.id)}
                        style={{cursor: 'pointer'}}
                      >
                        <div className='row w-100'>
                          <div className='col'>
                            <div className='d-flex align-items-center'>
                              <span>
                                {openAccordion === control.id ? (
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='#5A5A5A'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-down'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M6 9l6 6l6 -6' />
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='#5A5A5A'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M9 6l6 6l-6 6' />
                                  </svg>
                                )}
                              </span>
                              <span className='fs-6 fw-semibold mx-4'>
                                {getCodeFromFrameworkMapping(
                                  control.control_framework_mappings.split(',')[0].trim()
                                )}{' '}
                                {control.control_title}
                              </span>
                            </div>
                          </div>
                          <div className='col-auto d-flex align-items-center ml-auto'>
                            <div className='d-flex justify-content-end'>
                              {control.control_evidence_status === 'Deactivated' ? (
                                <div className='fw-semibold'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='20'
                                    height='20'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='#5A5A5A'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-eye-pause mx-1 mb-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0' />
                                    <path d='M13.022 17.945a9.308 9.308 0 0 1 -1.022 .055c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6c-.195 .325 -.394 .636 -.596 .935' />
                                    <path d='M17 17v5' />
                                    <path d='M21 17v5' />
                                  </svg>{' '}
                                  Deactivated
                                </div>
                              ) : control.control_evidence_status !== 'OK' ? (
                                <div className='fw-semibold'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='16'
                                    height='16'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='#EE3043'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-alert-triangle mb-1 mx-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M12 9v4' />
                                    <path d='M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z' />
                                    <path d='M12 16h.01' />
                                  </svg>{' '}
                                  Controls Unhealthy
                                </div>
                              ) : (
                                <div className='fw-semibold'>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='18'
                                    height='18'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='#0E9F6E'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    className='icon icon-tabler icons-tabler-outline icon-tabler-circle-check mb-1 mx-1'
                                  >
                                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                    <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                                    <path d='M9 12l2 2l4 -4' />
                                  </svg>{' '}
                                  OK
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {openAccordion === control.id && (
                        <div className='table-responsive mt-3'>
                          <table className='table table-hover'>
                            <thead>
                              <tr className='py-4 border-bottom fw-bold' style={{color: '#5A5A5A'}}>
                                <th>CONTROL</th>
                                <th>STATUS</th>
                                <th className='d-flex justify-content-center mx-20'>ACTION</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                className='border-bottom my-2 fw-bold'
                                style={
                                  currentUser?.account_type === 'Trial' && categoryCount > 1
                                    ? {filter: 'blur(4px)', pointerEvents: 'none'}
                                    : {color: '#171717'}
                                }
                              >
                                <td className='pt-6 pb-6'>
                                  <a
                                    href='#'
                                    className='text-dark fw-semibold text-hover-primary fs-6'
                                    onClick={(e) => {
                                      e.preventDefault()
                                      currentUser?.account_type === 'Trial'
                                        ? setShowModal(true)
                                        : handleOpenModal(control.id)
                                    }}
                                  >
                                    {control.control_title}{' '}
                                  </a>
                                </td>

                                <td className='pt-6 pb-6'>
                                  <div className='d-flex align-items-center'>
                                    {control.control_evidence_status === 'Deactivated' ? (
                                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                      <a
                                        href='#'
                                        className='badge fs-7 text-dark text-semibold m-1'
                                        style={{background: '#F6F6F6'}}
                                      >
                                        {control.control_evidence_status}
                                      </a>
                                    ) : (
                                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                      <a
                                        href='#'
                                        className={`badge fs-7 ${
                                          control.control_evidence_status === 'Ok' ||
                                          control.control_evidence_status === 'OK'
                                            ? 'badge-primary'
                                            : 'badge-danger'
                                        }`}
                                        style={{
                                          color:
                                            control.control_evidence_status === 'Ok' ||
                                            control.control_evidence_status === 'OK'
                                              ? '#0E9F6E'
                                              : '#EE3043',
                                          background:
                                            control.control_evidence_status === 'Ok' ||
                                            control.control_evidence_status === 'OK'
                                              ? '#E8F8F4'
                                              : '#FFF5F8',
                                        }}
                                      >
                                        {control.control_evidence_status === 'Ok' ||
                                        control.control_evidence_status === 'OK'
                                          ? 'OK'
                                          : 'Unhealthy'}
                                      </a>
                                    )}
                                    <span className='mx-2 fw-semibold'>
                                      {currentUser?.account_type === 'Trial'
                                        ? '0/0'
                                        : processControlValues(
                                            control.control_document,
                                            control.control_test,
                                            control.test_id,
                                            control.document_id,
                                            tests
                                          )}{' '}
                                      Test passing
                                    </span>
                                  </div>
                                </td>
                                <td className='pt-6 pb-6'>
                                  <div className='d-flex justify-content-center'>
                                    {currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? (
                                      <a
                                        href='#'
                                        onClick={(e) => {
                                          e.preventDefault()
                                          setShowModal(true)
                                        }}
                                        rel='noopener noreferrer'
                                        className='d-flex align-items-center text-gray-700 text-hover-primary'
                                      >
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          width='16'
                                          height='16'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='currentColor'
                                          strokeWidth='2'
                                          strokeLinecap='round'
                                          strokeLinejoin='round'
                                          className='icon icon-tabler icons-tabler-outline icon-tabler-external-link mb-1 mx-1'
                                        >
                                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                          <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
                                          <path d='M11 13l9 -9' />
                                          <path d='M15 4h5v5' />
                                        </svg>
                                        <span className='fs-7'>View in full Screen</span>
                                      </a>
                                    ) : (
                                      <a
                                        href={
                                          (currentAuditContext || currentUser?.role === 'Auditor') 
                                            ? '#'
                                            : `/controls/details/${control.id}`
                                        }
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={`d-flex align-items-center ${
                                          (currentAuditContext || currentUser?.role === 'Auditor') 
                                            ? 'text-gray-700 text-hover-primary'
                                            : 'text-gray-700 text-hover-primary'
                                        }`}
                                        onClick={(e) => {
                                          if (
                                            (currentAuditContext || currentUser?.role === 'Auditor') 
                                          ) {
                                            e.preventDefault()
                                            handleOpenModal(control.id)
                                          }
                                        }}
                                      >
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          width='16'
                                          height='16'
                                          viewBox='0 0 24 24'
                                          fill='none'
                                          stroke='currentColor'
                                          strokeWidth='2'
                                          strokeLinecap='round'
                                          strokeLinejoin='round'
                                          className='icon icon-tabler icons-tabler-outline icon-tabler-external-link mb-1 mx-1'
                                        >
                                          <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                          <path d='M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6' />
                                          <path d='M11 13l9 -9' />
                                          <path d='M15 4h5v5' />
                                        </svg>
                                        <span className='fs-7'>
                                        {(currentAuditContext || currentUser?.role === 'Auditor') 
                                            ? 'View details'
                                            : 'View in full Screen'}
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                ))}
              </div>
            )
          })
        ) : (
          <>
            {searchLength ? (
              <div className='text-center'>
                <div className='m-20'>
                  <img src='/media/buttons/emptystate.svg' alt='' />
                  <div className='m-5 fs-5'>
                    <p style={{fontWeight: 'bold'}}>No controls found</p>
                  </div>
                  <p className=''>
                    We couldn't find any controls that match your search. Maybe try a different
                    search!
                  </p>
                </div>
              </div>
            ) : (
              <div className='text-center '>
                {!showEmptyState ? (
                  <div
                    className='d-flex justify-content-center align-items-center text-center'
                    style={{
                      height: '100vh',
                      width: '100vw',
                      position: 'absolute',
                      top: '20vh',
                      left: '20vh',
                    }}
                  >
                    <TestListLoading />
                  </div>
                ) : (
                  <div className='m-20'>
                    <img src='/media/buttons/emptystate.svg' alt='' />
                    <div className=' fs-5'>
                      <p style={{fontWeight: 'bold'}}>No Controls</p>
                    </div>
                    <p>Connection error</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ToastContainer />
      <UpgradePlan show={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}

export default ViewReports
